'use client'

import { useModal } from '@/contexts/ModalContext'
import { useOrderSetup } from '@/contexts/OrderSetupContext'
import { MODAL_TYPES } from '@/libs/config/modal'
import { selectIsPreviewMode } from '@/redux/customisation'
import {
  selectCategoryEntities,
  selectItemEntities,
} from '@/redux/menu/selector'
import { selectStoreOrder } from '@/redux/order'
import { useAddItemToCartMutation } from '@/services/local'
import {
  getOptionsMinimumQuantity,
  getOrderItem,
  productStatus,
} from '@/utils/product'
import { useTranslate } from '@tolgee/react'
import { useSearchParams } from 'next/navigation'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  toast,
} from 'oddle-shop-monorepo.design-system'
import { useCallback, useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import ProductDetailsModal from './ProductDetailsModal'
import useTracker from '@/hooks/useTracker'
import { usePreviewNavigationContext } from '@/contexts/PreviewNavigationProvider'
import AddToCartToast from '@/components/common/AddToCartToast'
import useGetMenuProducts from '@/hooks/useGetMenuProducts'
import useResponsive from '@/hooks/useResponsive'
import { newrelicTrackUserAction } from '@/utils/newrelicLogger'

const Index = () => {
  const searchParams = useSearchParams()
  const orderSetup = useOrderSetup()
  const { closeModal, openModal } = useModal()
  const isPreviewMode = useSelector(selectIsPreviewMode)
  const { t } = useTranslate()
  const { notifyAddToCart } = useTracker()
  const { replaceWithPreview } = usePreviewNavigationContext()
  const { mobile: isMobile } = useResponsive()

  useGetMenuProducts(!searchParams?.get('productId'))

  const timeoutRefs = useRef<NodeJS.Timeout[]>([])

  const itemEntities = useSelector(selectItemEntities)
  const categoryEntities = useSelector(selectCategoryEntities)
  const storeOrder = useSelector(selectStoreOrder)

  const [addItemToCart] = useAddItemToCartMutation()

  const productIdQuery = searchParams?.get('productId')

  const product = productIdQuery
    ? itemEntities.entities?.[productIdQuery]
    : undefined
  const category = product?.categoryId
    ? categoryEntities.entities?.[product?.categoryId]
    : undefined

  const form = useForm({
    defaultValues: {
      totalQuantity: 1,
      selectedVariant: product?.variants?.[0]?.id || null,
      subitems: {} as any,
    },
  })

  const onClose = useCallback(() => {
    replaceWithPreview('/', { scroll: false })
    closeModal()
    const timeoutId = setTimeout(form.reset, 500)
    timeoutRefs.current.push(timeoutId)
  }, [closeModal, form.reset, replaceWithPreview])

  const proceedWithAddToCart = useCallback(
    async (values: any, product: Oms.MenuItem) => {
      try {
        const response = await addItemToCart({
          storeOrderId: storeOrder.data?.id,
          orderItem: getOrderItem(
            product,
            values.totalQuantity,
            values.selectedVariant,
            values.subitems
          ),
        }).unwrap()
        if (response?.data?.payload?.error) {
          openModal(MODAL_TYPES.ERROR_MODAL, {
            errorCode: t(response.data.payload.error),
            title: t('Oops...'),
            content: <span />,
            onConfirm: closeModal,
          })
          return
        }
        const variant = product.variants[0] as Oms.Variant
        notifyAddToCart({
          menuItem: product,
          orderItem: getOrderItem(
            product,
            values.totalQuantity,
            values.selectedVariant,
            values.subitems
          ),
          finalPrice: variant.price as number,
        })
        onClose()
        const timeoutId = setTimeout(() => {
          const items = [
            {
              name: product.name,
              quantity: values.totalQuantity,
              price: variant.price,
              imageUrl: product.imageFullUrls?.[0] || product.imageUrls?.[0],
            },
          ]
          if (!isMobile) {
            const toastId = toast(
              <AddToCartToast
                items={items}
                onClickGoCart={() => {
                  toast.dismiss(toastId)
                  openModal(MODAL_TYPES.CART_DRAWER)
                }}
              />,
              {
                position: 'top-right',
                duration: 2000,
                classNames: {
                  toast: '!bg-container !top-[45px]',
                  content: 'w-full',
                },
              }
            )
          }
        }, 1000)
        timeoutRefs.current.push(timeoutId)
      } catch (error) {
        toast.error(t('Cannot add item to cart'))
        console.error(error)
      }
    },
    [
      addItemToCart,
      isMobile,
      notifyAddToCart,
      onClose,
      openModal,
      storeOrder.data?.id,
      t,
    ]
  )

  const onSubmitHandler = async (values: any, product: Oms.MenuItem) => {
    if (isPreviewMode) {
      newrelicTrackUserAction('add-item-in-preview-mode', {
        productId: product.id,
        previewToken: searchParams?.get('previewToken'),
        storeOrderId: storeOrder.data?.id,
      })
      closeModal()
      onClose()
      toast.error(t('Cannot add item to cart on preview mode'))
      return
    }

    if (!storeOrder.data?.id || !product) {
      return
    }

    const optionsMinimumQuantity = getOptionsMinimumQuantity({
      product,
      selectedVariant: values.selectedVariant,
    })

    const invalidOption = Object.keys(optionsMinimumQuantity).find((key) => {
      if (!optionsMinimumQuantity[key]) return false
      return (
        (optionsMinimumQuantity[key] > 0 &&
          (values.subitems || {})[key] === undefined) ||
        optionsMinimumQuantity[key] >
          Object.keys((values.subitems || {})[key] || {}).reduce(
            (sum, item) => sum + values.subitems[key][item],
            0
          )
      )
    })

    if (invalidOption) {
      const optionElement = window.document.getElementById(invalidOption)
      if (optionElement) {
        optionElement.scrollIntoView({ behavior: 'smooth' })
      }
      return
    }

    if (
      product?.status === productStatus.UNAVAILABLE ||
      (!orderSetup.isOrderSetupComplete && !orderSetup.isForceFinished)
    ) {
      closeModal()
      onClose()
      const formValues = form.getValues()
      openModal(MODAL_TYPES.PICKUP_DELIVERY_SELECTION, {
        onFinished: () =>
          proceedWithAddToCart(formValues, {
            ...product,
            status: productStatus.AVAILABLE,
          }),
      })
      return
    }

    await proceedWithAddToCart(values, product)
  }

  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(clearTimeout)
      timeoutRefs.current = []
    }
  }, [])

  useEffect(() => {
    if (product?.variants && product.variants.length > 0) {
      form.reset({
        totalQuantity: 1,
        selectedVariant: product.variants[0]?.id,
        subitems: {},
      })
    }
  }, [product?.id, form])

  if (!product || !category) return null

  return (
    <Dialog
      open={Boolean(productIdQuery)}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent
        aria-describedby={undefined}
        className="bg-container text-body flex h-full max-w-full flex-col items-stretch gap-2.5 rounded-none p-0 shadow-sm duration-1000 md:h-auto md:w-[800px] md:rounded-2xl lg:!max-h-[90%]"
        onClickClose={onClose}
        invertColorCloseButton
      >
        <DialogTitle className="hidden" />
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit((onValid) =>
              onSubmitHandler(onValid, product)
            )}
            className="relative flex h-full flex-col items-stretch overflow-y-hidden rounded-none md:rounded-2xl"
          >
            <ProductDetailsModal product={product} category={category} />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export default Index
